import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Projects = () => {
  const { projects, projectsLoading, addProject, updateProject, removeProject, fetchProjects } = useApp();
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({ name: '', description: '', site_url: '' });
  const [creating, setCreating] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: '', name: '', description: '', site_url: '' });
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    // Fetch projects on mount if not already loaded
    if (projects.length === 0 && !projectsLoading) {
      fetchProjects();
    }
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!createFormData.name.trim()) {
      toast.error('O nome do projeto é obrigatório.');
      return;
    }
    
    if (createFormData.site_url && !createFormData.site_url.trim().startsWith('https://')) {
      toast.error('A URL do site deve começar com https://');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/projects`, createFormData);
      // Add to global state (this will also auto-select it)
      addProject(response.data);
      toast.success('Projeto criado com sucesso!');
      setCreateDialogOpen(false);
      setCreateFormData({ name: '', description: '', site_url: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar projeto';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEdit = (project) => {
    setEditFormData({
      id: project.id,
      name: project.name,
      description: project.description || '',
      site_url: project.site_url || ''
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.name.trim()) {
      toast.error('O nome do projeto é obrigatório.');
      return;
    }
    
    setEditing(true);
    try {
      const response = await axios.put(`${API_URL}/api/projects/${editFormData.id}`, {
        name: editFormData.name,
        description: editFormData.description,
        site_url: editFormData.site_url
      });
      // Update in global state
      updateProject(response.data);
      toast.success('Projeto atualizado com sucesso!');
      setEditDialogOpen(false);
      setEditFormData({ id: '', name: '', description: '', site_url: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar projeto';
      toast.error(message);
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto? Todos os dados associados serão perdidos.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/projects/${id}`);
      // Remove from global state
      removeProject(id);
      toast.success('Projeto excluído com sucesso!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir projeto';
      toast.error(message);
    }
  };

  if (projectsLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Projetos</h1>
          <p className="text-sm text-muted-foreground">Gerencie seus projetos de UX</p>
        </div>
        
        {/* Create Project Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-project-button">
              <Plus className="mr-2 h-4 w-4" />
              Novo Projeto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Criar Novo Projeto</DialogTitle>
                <DialogDescription>Preencha os dados do novo projeto</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="create-name">Nome *</Label>
                  <Input 
                    id="create-name" 
                    value={createFormData.name} 
                    onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})} 
                    placeholder="Ex: Meu Projeto UX"
                    disabled={creating}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-description">Descrição</Label>
                  <Textarea 
                    id="create-description" 
                    value={createFormData.description} 
                    onChange={(e) => setCreateFormData({...createFormData, description: e.target.value})}
                    placeholder="Descreva o projeto..."
                    disabled={creating}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-site-url">URL do site</Label>
                  <Input
                    id="create-site-url"
                    data-testid="create-site-url-input"
                    value={createFormData.site_url}
                    onChange={(e) => setCreateFormData({...createFormData, site_url: e.target.value})}
                    placeholder="https://www.loja.com.br"
                    disabled={creating}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    'Criar Projeto'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-semibold mb-2">Nenhum projeto criado</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Crie seu primeiro projeto para começar a trabalhar.
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Criar primeiro projeto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Card key={project.id} className="hover:shadow-md transition-shadow" data-testid={`project-${project.id}`}>
              <CardHeader>
                <CardTitle>{project.name}</CardTitle>
                <CardDescription>{project.description || 'Sem descrição'}</CardDescription>
                {project.site_url && (
                  <a
                    href={project.site_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-primary hover:underline break-all"
                    data-testid={`project-site-url-${project.id}`}
                  >
                    {project.site_url.replace(/^https:\/\//, '')}
                  </a>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Criado em {new Date(project.created_at).toLocaleDateString('pt-BR')}
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenEdit(project)}
                      data-testid={`edit-project-${project.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDelete(project.id)} 
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Editar Projeto</DialogTitle>
              <DialogDescription>Atualize os dados do projeto</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome *</Label>
                <Input 
                  id="edit-name" 
                  value={editFormData.name} 
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} 
                  placeholder="Ex: Meu Projeto UX"
                  disabled={editing}
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Descrição</Label>
                <Textarea 
                  id="edit-description" 
                  value={editFormData.description} 
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  placeholder="Descreva o projeto..."
                  disabled={editing}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-site-url">URL do site</Label>
                <Input
                  id="edit-site-url"
                  data-testid="edit-site-url-input"
                  value={editFormData.site_url}
                  onChange={(e) => setEditFormData({...editFormData, site_url: e.target.value})}
                  placeholder="https://www.loja.com.br"
                  disabled={editing}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} disabled={editing}>
                Cancelar
              </Button>
              <Button type="submit" disabled={editing}>
                {editing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar alterações'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
